const DEFAULT_MARKETING_ORIGIN = 'https://usefragments.com';
const DEFAULT_APP_ORIGIN = 'https://app.usefragments.com';
const DEFAULT_OG_IMAGE_PATH = '/og.png';
export const SITE_NAME = 'Fragments';
export const SITE_ALTERNATE_NAMES = [
  'UseFragments',
  'Fragments Cloud',
  'Fragments UI',
];

export type SeoHeadOptions = {
  title: string;
  description: string;
  path: string;
  canonicalPath?: string;
  keywords?: string[];
  index?: boolean;
  imagePath?: string;
  type?: 'website' | 'article';
};

function parseUrl(value: string | undefined, fallback: string) {
  if (!value) {
    return new URL(fallback);
  }

  try {
    return new URL(value);
  } catch {
    return new URL(fallback);
  }
}

export function getSiteUrl() {
  return parseUrl(import.meta.env.APP_URL, DEFAULT_MARKETING_ORIGIN);
}

export function getSiteOrigin() {
  return getSiteUrl().origin;
}

export function getMarketingOrigin() {
  const siteUrl = getSiteUrl();

  if (siteUrl.hostname === 'localhost' || siteUrl.hostname === '127.0.0.1') {
    return siteUrl.origin;
  }

  if (siteUrl.hostname.startsWith('app.')) {
    return DEFAULT_MARKETING_ORIGIN;
  }

  return siteUrl.origin;
}

export function isAppSite() {
  return getSiteUrl().hostname.startsWith('app.');
}

export function shouldIndexSite() {
  return !isAppSite();
}

export function getProductAppOrigin() {
  const siteUrl = getSiteUrl();

  if (siteUrl.hostname.startsWith('app.')) {
    return siteUrl.origin;
  }

  if (siteUrl.hostname === 'localhost' || siteUrl.hostname === '127.0.0.1') {
    return siteUrl.origin;
  }

  if (siteUrl.hostname === 'usefragments.com') {
    return DEFAULT_APP_ORIGIN;
  }

  return DEFAULT_APP_ORIGIN;
}

export function absoluteUrl(path: string) {
  return new URL(path, getSiteOrigin()).toString();
}

export function absoluteCanonicalUrl(path: string) {
  return new URL(path, getMarketingOrigin()).toString();
}

export function buildSeoHead(options: SeoHeadOptions) {
  const {
    title,
    description,
    path,
    canonicalPath = path,
    keywords = [],
    index = shouldIndexSite(),
    imagePath = DEFAULT_OG_IMAGE_PATH,
    type = 'website',
  } = options;

  const canonicalUrl = absoluteCanonicalUrl(canonicalPath);
  const imageUrl = absoluteUrl(imagePath);

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { name: 'application-name', content: SITE_NAME },
      { name: 'apple-mobile-web-app-title', content: SITE_NAME },
      {
        name: 'robots',
        content: index ? 'index, follow' : 'noindex, nofollow',
      },
      ...(keywords.length > 0
        ? [{ name: 'keywords', content: keywords.join(', ') }]
        : []),
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:type', content: type },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:image', content: imageUrl },
      { property: 'og:image:alt', content: `${SITE_NAME} preview` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: imageUrl },
      { name: 'twitter:url', content: canonicalUrl },
    ],
    links: [{ rel: 'canonical', href: canonicalUrl }],
  };
}

export function buildRobotsTxt() {
  if (!shouldIndexSite()) {
    return ['User-agent: *', 'Disallow: /'].join('\n');
  }

  return [
    'User-agent: *',
    'Allow: /',
    'Disallow: /api/',
    'Disallow: /sign-in',
    'Disallow: /sign-up',
    'Disallow: /onboarding',
    `Sitemap: ${absoluteUrl('/sitemap.xml')}`,
  ].join('\n');
}

export function buildSitemapXml(paths: string[]) {
  const urls = paths
    .map(
      (path) => `
  <url>
    <loc>${absoluteUrl(path)}</loc>
  </url>`,
    )
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">${urls}
</urlset>`;
}
